import React from 'react';
import { NavLink } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { CUSTOMER_ROLES } from '../../constants/roles';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11
import Icon from '../Common/Icon';
import CustomerSupportDrawer from '../Support/Customer/CustomerSupportDrawer';
import AgentTicketDrawer from '../Support/AgentTicketDrawer/AgentTicketDrawer';
import { actions as accountActions } from '../../actions/accountActions';
import { actions as navigationActions } from '../../actions/navigationActions';
import { actions as locationTaskActions } from '../../actions/locationTaskActions';
import { actions as toolTipActions } from '../../actions/toolTipActions';
import { actions as debugActions } from '../../actions/debugActions';
import * as types from '../../constants/actionTypes';
import {
	HEADER_MOBILE_BREAKPOINT,
	PUBLIC_IMAGE_URL,
	SUPPORT_TICKET_ID_URL_PARAM,
	CONFETTI_ANIMATION_TYPE,
	CONFETTI_CONFIG,
	EVENT_TYPE,
	MAX_NUM_CONFETTI,
	TASK_LIST_MENU_ITEMS,
	TASK_LIST_URL_PARAMS,
	PROJECT_MENU_ITEMS,
} from '../../constants/constants';
import Routes from '../../constants/routes';
import CustomerMenu from './CustomerMenu/CustomerMenu';
import AddSubscriptionDrawer from '../Common/AddSubscriptionDrawer/AddSubscriptionDrawer';
import './Header.scss';
import Notification from '../Common/Notification';
import { NavigateToRouteWithDashboardParams } from '../../helpers/routing';
import { getRedirectLocation } from '../../helpers/accountHelper';
import { API_ROOT } from '../../helpers/apiConfig';
import ReactGA from 'react-ga4';
import userPictureMask from '../../images/user_profile_top_nav.png';
import ToolTipBar from '../Common/ToolTipBar/ToolTipBar';
import ReactCanvasConfetti from 'react-canvas-confetti';

class CustomerHeader extends React.Component {
	constructor(props, context) {
		super(props, context);
		this.animationInstance = null;

		this.state = {
			supportTicketId: null,
			supportAgentDrawerOpen: false,
			supportDrawerOpen: false,
			addSubcriptionDrawerOpen: false,
			userMenuOpen: false,
			isStartDateFeatureActive: false,
			isProjectWidgetActive: false,
			isSSOUser: false,
			locationAccessCount: 0,
			selectedToolTip: null,
			maxLocationsSelected: 75,
			isLoading: false,
			projectHubProjectKey: null,
			projectMenuItems: [],
		};

		this.menuRef = React.createRef();
	}

	componentDidMount = async () => {
		document.addEventListener('mousedown', this.handleClickOutside);

		ReactGA.set({ dimension1: this.props.subscriptionId || '' });
		await this.loadHeaderNavigation();
		this.checkForToolTip();

		const searchParams = new URLSearchParams(this.props.history.location.search);
		if (searchParams.has(SUPPORT_TICKET_ID_URL_PARAM.AgentLink) && searchParams.getAll(SUPPORT_TICKET_ID_URL_PARAM.AgentLink)) {
			this.setState({ supportTicketId: searchParams.getAll(SUPPORT_TICKET_ID_URL_PARAM.AgentLink).toString(), supportAgentDrawerOpen: true });
		}

		document.addEventListener(EVENT_TYPE.ConfettiCannon, () => {
			if (window.confettiCount < MAX_NUM_CONFETTI)
				// eslint-disable-line
				this.fireConfetti(CONFETTI_ANIMATION_TYPE.Cannon);

			window.confettiCount++;
		});
	};

	componentWillUnmount() {
		document.removeEventListener('mousedown', this.handleClickOutside);
		document.removeEventListener(EVENT_TYPE.ConfettiCannon, () => this.fireConfetti(CONFETTI_ANIMATION_TYPE.Cannon));
	}

	componentDidUpdate = async (prevProps) => {
		const newSub = this.props.subscriptionId != prevProps.subscriptionId;
		const newLocation = this.props.locationKeys != prevProps.locationKeys;
		if (newSub || newLocation) {
			await this.loadHeaderNavigation();
		}

		// check for tooltips when the page changes
		if (prevProps.location.pathname != this.props.location.pathname) {
			this.checkForToolTip();
		}
	};

	isCustomerAdmin = () => {
		return [CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Moderator.name, CUSTOMER_ROLES.Owner.name].includes(this.props.user.customerRole);
	};

	checkForToolTip = () => {
		let location = this.props.location.pathname;
		let tips = this.props.user.toolTips;
		let foundTip = null;

		if (tips) {
			for (let i = 0; i < tips.length; i++) {
				if (location.indexOf(tips[i].routeName) != -1) {
					foundTip = tips[i];
					break;
				}
			}
		}
		// if nothing found, set to null so the ToolTipBar does not render
		this.setState({ selectedToolTip: foundTip });
	};

	dismissToolTip = async (updateDatabase) => {
		if (updateDatabase) {
			try {
				const response = await this.props.toolTipActions.dismissAndSave(this.state.selectedToolTip.id);
				if (response.type === types.DISMISS_TOOL_TIP_SUCCESS) {
					// redux is auto-updated when the request is made, just need to set the selectedToolTip to null here
					this.setState({ selectedToolTip: null });
				} else if (response.type === types.DISMISS_TOOL_TIP_FAILURE) {
					Notification('error', 'Error', response.payload.message);
				}
			} catch (error) {
				Notification('error', 'Error', error);
			}
		} else {
			this.props.toolTipActions.dismiss(this.state.selectedToolTip.id);
			this.setState({ selectedToolTip: null });
		}
	};

	loadHeaderNavigation = async () => {
		const navInfo = this.loadNavInfo();
		await Promise.all([navInfo, this.loadTasksMenu(navInfo)]);
	};

	loadNavInfo = async () => {
		try {
			/**
			 * In order to load brand colors and logo, we need a single brand to
			 * be the "current" brand.  We choose that to be the brand associated
			 * with the first location key stored in the task list state in Redux
			 */
			const locationKey = this.props.locationKeys && this.props.locationKeys.length ? this.props.locationKeys[0] : null;
			const request = {
				subscriptionId: this.props.subscriptionId,
				locationKey: locationKey,
			};
			const response = await this.props.navigationActions.info(request);
			if (response.type === types.LOAD_NAVIGATION_INFO_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			} else {
				const projectMenuItems = Object.values(PROJECT_MENU_ITEMS);
				this.setState({
					maxLocationsSelected: response.payload.myTasksAllLocationsThreshold,
					projectHubProjectKey: response.payload.projectHubProjectKey,
					projectMenuItems: response.payload.projectHubProjectKey
						? projectMenuItems
						: projectMenuItems.filter((item) => item.key !== PROJECT_MENU_ITEMS.ProjectHub.key), // remove "Open Project Control Hub" if no project key
				});

				/**
				 * Returned as well as stored in state because loadTasksMenu needs the
				 * threshold and setState is not applied by the time it reads it.
				 */
				return response.payload.myTasksAllLocationsThreshold;
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	loadTasksMenu = async (maxLocations) => {
		try {
			const request = {
				subscriptionId: this.props.subscriptionId,
				locationKeys: this.props.locationKeys,
			};
			const response = await this.props.navigationActions.loadTasksMenu(request);
			if (response.type === types.LOAD_TASKS_MENU_SUCCESS) {
				this.setState({
					isStartDateFeatureActive: response.payload.isStartDateActive,
					isProjectWidgetActive: response.payload.isProjectWidgetActive,
					isSSOUser: response.payload.isSSOUser,
					locationAccessCount: response.payload.locationAccessCount,
				});

				let menuItems = Object.values(TASK_LIST_MENU_ITEMS).map((x) => ({ ...x }));
				menuItems.forEach((x) => {
					if (x.value === TASK_LIST_MENU_ITEMS.Tasks.value) x.urlParams = response.payload.tasksUrlParams;
					if (x.value === TASK_LIST_MENU_ITEMS.MyTasks.value) {
						x.urlParams = {
							...response.payload.tasksUrlParams,
							responsibleUserName: [this.props.user.userDisplayName],
						};
					}
					if (x.value === TASK_LIST_MENU_ITEMS.MyTasksAllLocations.value) {
						x.urlParams = {
							...response.payload.tasksUrlParams,
							responsibleUserName: [this.props.user.userDisplayName],
							allLocations: [true],
						};
					}
					if (x.value === TASK_LIST_MENU_ITEMS.MyFollowingTasks.value) x.urlParams = response.payload.followingTasksUrlParams;
					if (x.value === TASK_LIST_MENU_ITEMS.PastDueTasks.value) x.urlParams = response.payload.pastDueUrlParams;
					if (x.value === TASK_LIST_MENU_ITEMS.DueSoonTasks.value) x.urlParams = response.payload.dueSoonUrlParams;
					if (x.value === TASK_LIST_MENU_ITEMS.StartingSoonTasks.value) x.urlParams = response.payload.startingSoonUrlParams;
					if (x.value === TASK_LIST_MENU_ITEMS.MilestoneTasks.value) x.urlParams = response.payload.milestonesUrlParams;
					if (x.value === TASK_LIST_MENU_ITEMS.MyFollowingUserTasks.value) x.urlParams = response.payload.myFollowingTasksUrlParams;
				});

				// Remove items that don't apply in certain contexts
				const removeMenuItem = (value) => {
					const index = menuItems.findIndex((x) => x.value === value);
					// splice(-1, 1) would drop the last item, so a miss has to be ignored
					if (index >= 0) menuItems.splice(index, 1);
				};

				const locationThreshold = (await maxLocations) ?? this.state.maxLocationsSelected;
				if (response.payload.locationAccessCount > locationThreshold)
					removeMenuItem(TASK_LIST_MENU_ITEMS.MyTasksAllLocations.value);
				if (!response.payload.isStartDateActive) removeMenuItem(TASK_LIST_MENU_ITEMS.StartingSoonTasks.value);

				// Store normal/regular menu items in redux
				await this.props.locationTaskActions.setTasksMenuItems(menuItems);
			} else if (response.type === types.LOAD_TASKS_MENU_FAILURE) {
				Notification('error', 'Error', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	onTasksMenuClick = async (menuItemValue) => {
		switch (menuItemValue.value) {
			case TASK_LIST_MENU_ITEMS.Tasks.value:
			case TASK_LIST_MENU_ITEMS.AllTasks.value:
			case TASK_LIST_MENU_ITEMS.MyTasks.value:
			case TASK_LIST_MENU_ITEMS.MyFollowingTasks.value:
			case TASK_LIST_MENU_ITEMS.PastDueTasks.value:
			case TASK_LIST_MENU_ITEMS.DueSoonTasks.value:
			case TASK_LIST_MENU_ITEMS.StartingSoonTasks.value:
			case TASK_LIST_MENU_ITEMS.MilestoneTasks.value:
			case TASK_LIST_MENU_ITEMS.MyFollowingUserTasks.value: {
				// switch used for concrete pattern matching validation of available menu items
				// if it's one of these values move on, otherwise default with error
				const currentLocationKeys = this.props.taskListState.searchParams.locationKeys || [];

				const searchParams = {
					...this.props.taskListState.searchParams,
					locationKeys: currentLocationKeys.length > 1 ? [] : currentLocationKeys,
				};

				await this.props.locationTaskActions.setLocationFilterState(searchParams);
				break;
			}
			case TASK_LIST_MENU_ITEMS.MyTasksAllLocations.value:
				break;
			default: {
				Notification('error', 'Error', 'Unknown Tasks Menu option');
				return;
			}
		}

		if (menuItemValue) {
			await this.props.locationTaskActions.setTasksMenuSelected(menuItemValue);
			const menuItem =
				this.props.taskListState.tasksMenuItems[this.props.taskListState.tasksMenuItems.findIndex((x) => x.value === menuItemValue.value)];
			NavigateToRouteWithDashboardParams(Routes.Tasks, menuItem.urlParams, false);
		}
	};

	onProjectsMenuClick = (menuItemValue) => {

		if (!this.isCustomerAdmin()) {
			this.props.history.push(Routes.PchNotYet);
			return;
		}

		switch (menuItemValue) {
			case PROJECT_MENU_ITEMS.ProjectHub.key: {
				this.goToProjectHub();
				break;
			}
			case PROJECT_MENU_ITEMS.AddProject.key: {
				this.props.history.push(Routes.NewProjectWizard);
				break;
			}
			case PROJECT_MENU_ITEMS.ViewAllProjects.key: {
				this.props.history.push(Routes.AdminHome + '/projects');
				break;
			}
			default: {
				Notification('error', 'Error', 'Unknown Tasks Menu option');
				return;
			}
		}
	};

	goToProjectHub = () => {
		if (!this.isCustomerAdmin()) {
			this.props.history.push(Routes.PchNotYet);
			return;
		}

		if (this.state.projectHubProjectKey) {
			this.props.history.push(`${Routes.NewProjectManagerDashboard}/${this.state.projectHubProjectKey}`);
		}
	};

	isCompact = () => {
		return this.props.windowWidth < HEADER_MOBILE_BREAKPOINT;
	};

	showSupportDrawer = () => {
		this.setState({ supportDrawerOpen: !this.state.supportDrawerOpen });
		this.setState({ userMenuOpen: false });
	};

	showUserMenu = () => {
		this.setState({ userMenuOpen: true });
	};

	closeUserMenu = () => {
		this.setState({ userMenuOpen: false });
	};

	goToSelectedRoute = (route) => {
		this.setState({ userMenuOpen: false });

		if (route === Routes.Support) {
			this.showSupportDrawer();
		} else if (route.indexOf('http') != -1) {
			var win = window.open(route, '_blank');
			win.opener = null;
			win.focus();
		} else {
			this.props.history.push(route);
		}
	};

	onAddSubscription = () => {
		this.setState({ addSubcriptionDrawerOpen: true });
		this.setState({ userMenuOpen: false });
	};

	onAddSubsriptionComplete = () => {
		this.setState({ addSubcriptionDrawerOpen: false });
	};

	onAddSubscriptionCancel = () => {
		this.setState({ addSubcriptionDrawerOpen: false });
	};

	onChangeSubscription = async (event) => {
		const id = event.target.value;
		const newSub = this.props.user.activeUserSubscriptions.find((x) => x.id == id);
		// set the selected subscription
		this.props.accountActions.setCurrentSubscription(newSub.id, newSub.name, newSub.customerRoleName, newSub.hasMultipleLocations);
		// clear out any locationKeys for the previous subscription
		const searchParams = {
			...this.props.taskListState.searchParams,
			locationKeys: [],
		};
		await this.props.locationTaskActions.setLocationFilterState(searchParams);

		await this.props.locationTaskActions.setLocationSelectorFilters([], null);

		this.props.history.push(getRedirectLocation(newSub.customerRoleName, newSub.hasMultipleLocations));
	};

	renderSupportDrawer = () => {
		return <CustomerSupportDrawer onComplete={this.showSupportDrawer} />;
	};

	renderSupportAgentDrawer = () => {
		return <AgentTicketDrawer ticketId={this.state.supportTicketId} onComplete={this.toggleSupportAgentDrawer} />;
	};

	toggleSupportAgentDrawer = () => {
		this.setState({ supportAgentDrawerOpen: !this.state.supportAgentDrawerOpen });
	};

	animateConfetti = (particleRatio, opts) => {
		this.animationInstance &&
			this.animationInstance({
				...opts,
				origin: { y: 0.7 },
				particleCount: Math.floor(200 * particleRatio),
			});
	};

	fireConfetti = (type) => {
		switch (type) {
			case CONFETTI_ANIMATION_TYPE.Realistic:
				this.animateConfetti(0.25, {
					spread: 26,
					startVelocity: 55,
				});

				this.animateConfetti(0.2, {
					spread: 60,
				});

				this.animateConfetti(0.35, {
					spread: 100,
					decay: 0.91,
					scalar: 0.8,
				});

				this.animateConfetti(0.1, {
					spread: 120,
					startVelocity: 25,
					decay: 0.92,
					scalar: 1.2,
				});

				this.animateConfetti(0.1, {
					spread: 120,
					startVelocity: 45,
				});
				break;
			case CONFETTI_ANIMATION_TYPE.Cannon:
				this.animateConfetti(1, {
					spread: 70,
				});
				break;
		}
	};

	getInstance = (instance) => {
		this.animationInstance = instance;
	};

	buildQueryParams = (params) => {
		const searchParams = new URLSearchParams();

		Object.entries(params).forEach(([key, value]) => {
			const shortKey = TASK_LIST_URL_PARAMS[key] || key;

			if (Array.isArray(value)) {
				value.forEach((v) => searchParams.append(shortKey, v));
			} else {
				searchParams.append(shortKey, value);
			}
		});

		return searchParams.toString();
	};

	goToTasksWithParams = () => {
		this.props.locationTaskActions.setTasksMenuSelected(TASK_LIST_MENU_ITEMS.Tasks.value);
		const menuItem = this.props.tasksMenuItems.find((x) => x.value === TASK_LIST_MENU_ITEMS.Tasks.value);

		if (menuItem?.urlParams) {
			const queryString = this.buildQueryParams(menuItem.urlParams);
			this.props.history.push(`${Routes.Tasks}?${queryString}`);
		} else {
			this.props.history.push(Routes.Tasks);
		}
	};

	handleClickOutside = (event) => {
		if (this.menuRef.current && !this.menuRef.current.contains(event.target)) {
			this.closeUserMenu();
		}
	};

	render() {
		/**
		 * This is a bit odd:  We have a Corporate Summary nav menu and a Dashboard menu, both of which are really considered
		 * dashboards.  We'd like the URLs to reflect that by starting with /dashboard/, but to still highlight the proper portion
		 * of the nav menu as the current one, so here we're explictly setting the current class if the route starts with the
		 * complete route for the Corporate or Location Dashboard
		 */
		const corporateClass = 'app-link' + (this.props.location.pathname.startsWith(Routes.DashboardCorporate) ? ' current' : '');
		const locationClass = 'app-link' + (this.props.location.pathname.startsWith(Routes.DashboardLocation) ? ' current' : '');
		const tasksClass = 'app-link' + (this.props.location.pathname.startsWith(Routes.Tasks) ? ' current' : '');
		const reportsClass = 'app-link' + (this.props.location.pathname.startsWith(Routes.Reports) ? ' current' : '');
		const projectsClass =
			'app-link' +
			(this.props.location.pathname.startsWith(Routes.NewProjectManagerDashboard) ||
				this.props.location.pathname.startsWith(Routes.ProjectManagerDashboard)
				? ' current'
				: '');
		const profileClass = 'app-link' + (this.props.location.pathname.startsWith(Routes.Profile) ? ' current' : '');
		const myWorkspaceClass = 'app-link' + (this.props.location.pathname.startsWith(Routes.MyWorkspace) ? ' current' : '');

		let logo = null;
		const shouldDisplayBrandLogo =
			this.props.location.pathname.startsWith(Routes.DashboardLocation) || this.props.location.pathname.startsWith(Routes.Tasks);
		if (shouldDisplayBrandLogo && this.props.navInfo.brandLogo) {
			logo = API_ROOT + PUBLIC_IMAGE_URL + this.props.navInfo.brandLogo;
		} else if (this.props.navInfo.subscriptionLogo) {
			logo = API_ROOT + PUBLIC_IMAGE_URL + this.props.navInfo.subscriptionLogo;
		}

		let currentLogoColor = null;
		if (shouldDisplayBrandLogo && this.props.navInfo.brandBackgroundColor) {
			currentLogoColor = this.props.navInfo.brandBackgroundColor;
		} else if (this.props.navInfo.subscriptionLogoColor) {
			currentLogoColor = this.props.navInfo.subscriptionLogoColor;
		}
		const logoStyle = currentLogoColor ? { backgroundColor: currentLogoColor } : {};

		let userProfileBackground = null;
		// dymanic css because of the dynamic background. It's a background so we can have the circle overlay
		if (this.props.user.userProfilePicture) {
			userProfileBackground = {
				backgroundImage: 'url("' + this.props.user.userProfilePicture + '")',
				width: '40px',
				height: '40px',
				backgroundSize: 'contain',
				display: 'inline-block',
				marginTop: '12px',
				verticalAlign: 'top',
			};
		}

		return (
			<div>
				{this.isCompact() ? (
					<nav className='navbar navbar-expand-lg navbar-dark'>
						<NavLink className='navbar-brand' exact to={Routes.Root}>
							<div className='navbar-logo-wrapper' style={logoStyle}>
								{logo && <img src={logo} alt='Home' />}
							</div>
						</NavLink>
						<ul className='navbar-nav mr user-menu-link'>
							<li className='nav-item ' onClick={this.showUserMenu}>
								<Icon name='bars me-2' />
							</li>
						</ul>
					</nav>
				) : (
					<nav className='navbar navbar-expand-lg navbar-dark'>
						<NavLink className='navbar-brand' exact to={Routes.Root}>
							<div className='navbar-logo-wrapper' style={logoStyle}>
								{logo && <img src={logo} alt='Home' />}
							</div>
						</NavLink>

						<ul className='navbar-nav mr-auto'>
							<div className={projectsClass + ' dropdown'}>
								<div className='app-link-item'>
									<a
										href='#'
										onClick={(e) => {
											e.preventDefault();
											this.goToProjectHub();
										}}>
										Projects
									</a>
									<ul className='dropdown-menu'>
										{this.state.projectMenuItems.map((menuItem, i) => {
											return (
												<li
													key={`menu-item-${i}`}
													className='dropdown-menu-item'
													onClick={() => {
														this.onProjectsMenuClick(menuItem.key);
													}}>
													{menuItem.title}
												</li>
											);
										})}
									</ul>
								</div>
							</div>

							{this.props.user.hasMultipleLocations && (
								<div className={corporateClass}>
									<div className='app-link-item'>
										<NavLink className='' to={Routes.DashboardCorporate}>
											Company Overview
										</NavLink>
									</div>
								</div>
							)}

							<div className={locationClass}>
								<div className='app-link-item'>
									<NavLink to={Routes.DashboardLocation}>Dashboard</NavLink>
								</div>
							</div>

							<div className={tasksClass + ' dropdown tasks-menu'}>
								<div className='app-link-item'>
									<a
										href='#'
										onClick={(e) => {
											e.preventDefault();
											this.goToTasksWithParams();
										}}>
										Tasks
									</a>
									<ul className='dropdown-menu'>
										{this.props.taskListState.tasksMenuItems.map((menuItem, i) => {
											if (menuItem.displayName === 'Milestones' || menuItem.displayName === 'My Following Tasks') return null;

											return (
												<li
													key={`menu-item-${i}`}
													className='dropdown-menu-item'
													onClick={async () => {
														await this.onTasksMenuClick(menuItem);
													}}>
													{menuItem.displayName}
												</li>
											);
										})}
									</ul>
								</div>
							</div>

							<div className={reportsClass}>
								<div className='app-link-item'>
									<NavLink to={Routes.Reports}>Reports</NavLink>
								</div>
							</div>
						</ul>

						<div className='navbar-nav ms-auto'>
							<div className={myWorkspaceClass}>
								<div className='app-link-item'>
									<NavLink to={Routes.MyWorkspace}>My Workspace</NavLink>
								</div>
							</div>
						</div>

						<span className='pacer-vertical-line-separator'></span>

						<ul className='navbar-nav mr user-menu-link'>
							<div className={profileClass} onClick={this.showUserMenu}>
								<div className='app-link-item'>
									<div className='name-wrapper'>
										<div className='inner'>
											<span className='user-name'>{this.props.user.userDisplayName}</span>
											<span className='subscription-name'>{this.props.user.currentSubscriptionName}</span>
										</div>
									</div>
									{this.props.user.userProfilePicture ? (
										<div style={userProfileBackground}>
											<img src={userPictureMask} height='40' width='40' />
										</div>
									) : (
										<div className='icon-wrapper'>
											<div className='inner'>
												<Icon name='user' />
											</div>
										</div>
									)}
								</div>
							</div>
						</ul>
					</nav>
				)}
				{this.state.supportAgentDrawerOpen && this.renderSupportAgentDrawer()}
				{this.state.supportDrawerOpen && this.renderSupportDrawer()}
				{this.state.userMenuOpen && (
					<div ref={this.menuRef}>
						<CustomerMenu
							isSSOUser={this.state.isSSOUser}
							onAddSubscription={this.onAddSubscription}
							onChangeSubscription={this.onChangeSubscription}
							closeUserMenu={this.closeUserMenu}
							showSupportDrawer={this.showSupportDrawer}
							locationTaskActions={this.props.locationTaskActions}
							history={this.props.history}
						/>
					</div>
				)}
				{this.state.addSubcriptionDrawerOpen && (
					<AddSubscriptionDrawer
						user={this.props.user}
						onComplete={this.onAddSubsriptionComplete}
						onCancel={this.onAddSubscriptionCancel}
					/>
				)}
				{this.state.selectedToolTip && (
					<ToolTipBar toolTip={this.state.selectedToolTip} location={this.props.location} dismissCallback={this.dismissToolTip} />
				)}
				<ReactCanvasConfetti refConfetti={this.getInstance} style={CONFETTI_CONFIG} />
			</div>
		);
	}
}

CustomerHeader.propTypes = {
	subscriptionId: PropTypes.string.isRequired,
	locationKeys: PropTypes.array.isRequired,
	history: PropTypes.object.isRequired,
	accountActions: PropTypes.object.isRequired,
	navigationActions: PropTypes.object.isRequired,
	locationTaskActions: PropTypes.object.isRequired,
	toolTipActions: PropTypes.object.isRequired,
	debugActions: PropTypes.object.isRequired,
	taskListState: PropTypes.object.isRequired,
	tasksMenuItems: PropTypes.array.isRequired,
	windowWidth: PropTypes.number,
	user: PropTypes.object,
	location: PropTypes.object,
	navInfo: PropTypes.object.isRequired,
};

function mapStateToProps(state, ownProps) {
	let locationKeys = [];
	if (
		state.taskListState &&
		state.taskListState.searchParams &&
		state.taskListState.searchParams.locationKeys &&
		state.taskListState.searchParams.locationKeys
	) {
		locationKeys = state.taskListState.searchParams.locationKeys;
	}

	let tasksMenuItems = [];
	if (state.taskListState && state.taskListState.tasksMenuItems && state.taskListState.tasksMenuItems.length) {
		tasksMenuItems = state.taskListState.tasksMenuItems;
	}

	return {
		subscriptionId: state.user.currentSubscriptionId ? state.user.currentSubscriptionId.toString() : '',
		locationKeys: locationKeys,
		user: state.user,
		windowWidth: state.window.width,
		history: ownProps.history,
		navInfo: state.navInfo,
		taskListState: state.taskListState,
		tasksMenuItems: tasksMenuItems,
	};
}

function mapDispatchToProps(dispatch) {
	return {
		accountActions: bindActionCreators(accountActions, dispatch),
		navigationActions: bindActionCreators(navigationActions, dispatch),
		locationTaskActions: bindActionCreators(locationTaskActions, dispatch),
		debugActions: bindActionCreators(debugActions, dispatch),
		toolTipActions: bindActionCreators(toolTipActions, dispatch),
	};
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerHeader));
